import { useEffect, useRef, useState } from 'react';
import { IChart } from 'components/charts/Charts.interface';
import ChartService from 'services/chart/chart.service';

// Interfaz base
interface IChartWithMeta extends IChart {
  total: number;
  __meta: {
    title: string;
    description: string;
    type: string;
    enabled: boolean;
    year?: string; // Propiedad opcional
  };
  __data?: {
    data: number[];
  }[];
  variacion?: string;
}

// Extensión para forzar que la propiedad 'data' tenga la forma IChartDataExtended
interface IChartWithMetaExtended extends IChartWithMeta {
  data: IChartDataExtended;
}

interface IChartDataExtended {
  labels: string[];
  datasets: any[];
  openResponses?: string[];
}

export default function useCharts(url: string, filters: any, id: string = '') {
  const [title, setTitle] = useState('');
  const firstUpdate = useRef(true);

  // Estado para los charts
  const emptyGraph: IChartWithMeta[] = [];
  const [charts, setCharts] = useState(emptyGraph);

  // Estado de carga
  const [isLoadingCharts, setIsLoadingCharts] = useState(true);

  // Flag para verificar si el componente sigue montado
  const isMounted = useRef(true);

  async function getChartsLinks(abortController: any) {
    return await ChartService.getChartsLinks(url, filters, id, abortController);
  }

  useEffect(() => {
    const abortController = new AbortController();
    isMounted.current = true;
    setIsLoadingCharts(true);

    async function fetchGraphs() {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }

      if (!filters || filters.length === undefined) {
        return;
      }

      const { links, metaTitle } = await getChartsLinks(abortController.signal);

      if (isMounted.current) {
        setTitle(metaTitle);
        setIsLoadingCharts(false);
      }

      // Construimos un arreglo inicial de charts
      const parsedCharts = links?.map((chart: any) => ({
        url: chart.__link,
        isLoading: true,
        total: 0
      })) ?? [];

      if (isMounted.current) {
        setCharts(parsedCharts);
        updateCharts(parsedCharts);
      }
    }

    fetchGraphs();

    return () => {
      isMounted.current = false;
      abortController.abort();
    };
  }, [filters]);

  /**
   * Actualiza cada chart en el array, haciendo una llamada al servicio
   * y moviendo la propiedad openResponses a 'data' para que el front la lea como chart.data.openResponses.
   */
  const updateCharts = (parsedCharts: IChartWithMeta[]) => {
    parsedCharts?.map?.(async (chartURL, index) => {
      if (chartURL.url && isMounted.current) {
        const graphData = (await ChartService.getChart(chartURL.url, filters)) as any;
  
        // Logs detallados:
        console.log('=== RAW graphData ===', graphData);
        console.log('graphData.openResponses (root):', graphData.openResponses);
        console.log('graphData.data:', graphData.data);
        console.log('graphData.data?.openResponses:', graphData.data?.openResponses);
  
        if (isMounted.current) {
          setCharts((prev) => {
            const tempArray = [...prev];
  
            tempArray[index] = {
              ...graphData,
              total: graphData.data?.datasets?.[0]?.data?.[0] || 0,
              __meta: {
                title: graphData.name || 'Sin título',
                description: graphData.description || 'Sin descripción',
                type: 'card',
                enabled: true,
              },
              data: {
                ...graphData.data,
                openResponses: graphData.openResponses || graphData.data?.openResponses || []
              }
            };
  
            console.log('Updated Chart:', tempArray[index]);
            return tempArray;
          });
        }
      }
    });
  };
  

  return { isLoadingCharts, charts, title };
}
